<script setup lang="ts">
import { computed, ref, watch } from 'vue'
import { useRoute, useRouter } from 'vue-router'
import { useQuery } from '@tanstack/vue-query'
import { Camera } from 'lucide-vue-next'
import type { MediaPhase } from '@ck/domain'
import { CkAlert, CkLightbox, CkTabs } from '@ck/ui'
import { mediaApi, workOrdersApi } from '@ck/api-client'
import { useWorkOrderMedia } from '../composables/useWorkOrderMedia'
import BottomNav from '../components/BottomNav.vue'

const route = useRoute()
const router = useRouter()

const workOrderQuery = useQuery({ queryKey: ['my-work-orders'], queryFn: () => workOrdersApi.list() })
const vehicles = computed(() => workOrderQuery.data.value?.items ?? [])
const selectedId = ref<string | undefined>(typeof route.query.os === 'string' ? route.query.os : undefined)
watch(vehicles, (list) => { if (!selectedId.value && list[0]) selectedId.value = list[0].id }, { immediate: true })
const selected = computed(() => vehicles.value.find((v) => v.id === selectedId.value))

function selectVehicle(id: string) {
  selectedId.value = id
  router.replace({ query: { ...route.query, os: id } })
}

const workOrderId = computed(() => selectedId.value)
const { mediaQuery, photoUrls, remove, removingId, removeError } = useWorkOrderMedia(workOrderId)

const phase = ref<MediaPhase>('before')
const phaseTabs = [
  { key: 'before', label: 'Antes' },
  { key: 'during', label: 'Durante' },
  { key: 'after', label: 'Depois' }
] as const
const photosInPhase = computed(() => (mediaQuery.data.value ?? []).filter((asset) => asset.phase === phase.value))

const openId = ref<string | null>(null)
const openAsset = computed(() => mediaQuery.data.value?.find((asset) => asset.id === openId.value))
async function removeOpen() {
  if (!openId.value) return
  const id = openId.value
  await remove(id)
  if (!removeError.value) openId.value = null
}

const fileInput = ref<HTMLInputElement | null>(null)
const uploading = ref(false)
const uploadError = ref(false)
async function onFileSelected(event: Event) {
  const input = event.target as HTMLInputElement
  const file = input.files?.[0]
  input.value = ''
  if (!file || !selectedId.value) return
  uploading.value = true
  uploadError.value = false
  try {
    await mediaApi.upload({ file, workOrderId: selectedId.value, visibleToCustomer: true, phase: phase.value })
    await mediaQuery.refetch()
  } catch {
    uploadError.value = true
  } finally {
    uploading.value = false
  }
}
</script>

<template>
  <div class="app">
    <header><h1>Registro fotográfico</h1></header>

    <div v-if="vehicles.length > 1" class="switcher">
      <button v-for="v in vehicles" :key="v.id" type="button" :class="{ active: v.id === selectedId }" @click="selectVehicle(v.id)">{{ v.vehicle.plate }}</button>
    </div>
    <p v-if="selected" class="ck-eyebrow vehicle-label">{{ selected.vehicle.brand }} {{ selected.vehicle.model }} · {{ selected.number }}</p>

    <CkAlert v-if="workOrderQuery.isError.value || mediaQuery.isError.value" tone="error">Não foi possível carregar as fotos. Tente novamente em instantes.</CkAlert>
    <p v-else-if="workOrderQuery.isPending.value" class="status-message">Carregando…</p>
    <p v-else-if="vehicles.length === 0" class="status-message">Nenhuma ordem de serviço atribuída a você no momento.</p>
    <template v-else>
      <CkTabs :tabs="phaseTabs" :model-value="phase" @update:model-value="(v) => (phase = v as MediaPhase)"/>
      <CkAlert v-if="uploadError" tone="error" class="upload-error">Não foi possível enviar a foto. Tente novamente.</CkAlert>
      <input ref="fileInput" type="file" accept="image/*" capture="environment" class="sr-only" @change="onFileSelected">
      <div class="grid">
        <button v-for="asset in photosInPhase" :key="asset.id" type="button" class="thumb" @click="openId = asset.id">
          <img :src="photoUrls[asset.id]" :alt="asset.fileName">
        </button>
        <button type="button" class="add" :disabled="uploading" @click="fileInput?.click()">
          <Camera :size="22"/><span>{{ uploading ? 'Enviando…' : 'Adicionar' }}</span>
        </button>
      </div>
      <p v-if="photosInPhase.length === 0" class="status-message empty">Nenhuma foto desta fase ainda.</p>
    </template>
    <CkLightbox
      v-if="openAsset && photoUrls[openAsset.id]"
      :src="photoUrls[openAsset.id]!"
      :alt="openAsset.fileName"
      removable
      :removing="removingId === openAsset.id"
      @close="openId = null"
      @remove="removeOpen"
    />
    <BottomNav/>
  </div>
</template>

<style scoped>
.app{max-width:620px;min-height:100vh;margin:auto;padding:calc(20px + env(safe-area-inset-top)) 18px calc(94px + env(safe-area-inset-bottom));background:var(--ck-cream-100)}
header{margin-bottom:14px}
header h1{margin:0;font-size:28px}
.switcher{display:flex;gap:6px;flex-wrap:wrap;margin-bottom:8px}
.switcher button{border:1px solid var(--ck-cream-300);border-radius:var(--radius-full);background:var(--ck-cream-50);color:var(--ck-text-muted);font-family:var(--font-mono);font-size:11px;padding:5px 11px;cursor:pointer}
.switcher button.active{border-color:var(--ck-orange-500);color:var(--ck-orange-500);font-weight:700}
.vehicle-label{margin:0 0 14px}
.status-message{padding:20px 0;color:var(--ck-text-muted);font-size:13px}
.status-message.empty{padding:24px 0;text-align:center;font-family:var(--font-serif);font-style:italic;color:var(--ck-text-faint)}
.upload-error{margin-top:12px}
.grid{display:grid;grid-template-columns:repeat(2,1fr);gap:10px;margin-top:14px}
.thumb{padding:0;border:0;border-radius:var(--radius-md);overflow:hidden;cursor:pointer;background:var(--ck-ink-800)}
.thumb img{display:block;width:100%;height:150px;object-fit:cover}
.add{display:flex;flex-direction:column;align-items:center;justify-content:center;gap:6px;height:150px;border:2px dashed var(--ck-cream-400);border-radius:var(--radius-md);background:transparent;color:var(--ck-text-muted);font-size:13px;font-weight:600;cursor:pointer}
.add svg{color:var(--ck-orange-500)}
.add:disabled{opacity:.6;cursor:not-allowed}
.sr-only{position:absolute;width:1px;height:1px;overflow:hidden;clip:rect(0,0,0,0)}
</style>
